import React, { type ComponentProps, useState, type ReactNode, useEffect } from 'react'
import { TreeSelect as AntTreeSelect, type TreeSelectProps } from 'antd'
import { Tag } from '../../../shared/webcomponents/react-wrappers/Tag'
import { CategoryPicker } from '../../../shared/webcomponents/react-wrappers/CategoryPicker'
import { Pulse } from '../ui/Pulse'
import type { DefaultOptionType, SimpleModeConfig } from 'rc-tree-select/lib/TreeSelect'
import { Translations } from '../../../shared/i18n/Translations'
import { v4 as uuidv4 } from 'uuid'
import { getConfigForMultiParents, hasTagNotBeenRendered } from './tree-multi-parent'
import { ensureArray } from '../../../shared/lib/helpers.no-deps'
import { DropdownArrowIcon } from './icon-overrides'

type TreeConfig = ComponentProps<typeof AntTreeSelect>
type CustomTagProps = Parameters<TreeConfig['tagRender']>[number]

export type TreeSelectOption = DefaultOptionType & {
    category?: string
    originalLabel?: string
}

interface Props extends TreeSelectProps<any, TreeSelectOption> {
    loading?: boolean
    sortTreeDataAlphabetically?: boolean
    /** by default no duplicate values are allowed in ant tree but if it's needed e.g. for unit with multiple parents.
     * only usable with `treeDataSimpleMode == falsy` and `typeof value = string` */
    multiParents?: boolean
    categories?: string[]
    /** return null to use the default not found view */
    notFoundContent?: (query: string, categories?: string[]) => ReactNode
}

TreeSelect.defaultProps = {
    sortTreeDataAlphabetically: true,
}

export function TreeSelect(props: Props) {
    const [selectedCategories, setSelectedCategories] = useState<string[]>(props.categories ?? [])
    const [searchQuery, setSearchQuery] = React.useState<string>('')
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)

    const { loading, multiParents, categories, notFoundContent, ...treeComponentProps } = props // otherwise warning bc of unknown props for ant treeselect

    const staticConfig: TreeConfig = {
        allowClear: { clearIcon: <zaptic-icon data-testid="clear-all-treeselect-input" icon-name="inverted-cross" /> },
        showCheckedStrategy: AntTreeSelect.SHOW_CHILD,
        multiple: true,
        treeCheckable: true,
        size: 'large',
        maxTagCount: 8,
        listHeight: 300,
        ...treeComponentProps,
        fieldNames: { label: 'label', value: 'value', children: 'children', ...(treeComponentProps.fieldNames ?? {}) },
    }

    const nodesToRender = React.useMemo(() => {
        const rootDataFilteredByCategory = props.treeData.filter(byCategory(selectedCategories, hasCategories(props)))
        return props.treeDataSimpleMode
            ? convertSimpleData1DArrayToDisplayable(rootDataFilteredByCategory)
            : convertTreeDataMultiDimensionArrayToDisplayable(rootDataFilteredByCategory)
    }, [props.treeData, props.categories, selectedCategories, props.treeDataSimpleMode, props.multiParents])

    const flattenedData = React.useMemo(() => nodesToRender.flatMap(falattenTreeOption), [nodesToRender])

    const hasIcons = React.useMemo(() => flattenedData.some((node) => node.icon), [flattenedData])
    const treeConfig: TreeConfig = {
        treeNodeFilterProp: staticConfig.fieldNames.label, //search by label by default
        treeIcon: hasIcons,
        ...staticConfig,
        ...(props.multiParents &&
            getConfigForMultiParents({
                value: props.value,
                onChange: props.onChange,
                flattenedData,
                valueFieldName: staticConfig.fieldNames.value,
            })),
        dropdownRender,
        switcherIcon: DropdownArrowIcon,
        onDropdownVisibleChange: (isDropdownVisible) => setIsDropdownOpen(isDropdownVisible),
        suffixIcon: DropdownArrowIcon({ expanded: isDropdownOpen }),
        tagRender,
        treeData: nodesToRender,
        onSearch: setSearchQuery,
        notFoundContent:
            props.notFoundContent?.(searchQuery, selectedCategories) ?? defaultNotFoundContent(searchQuery),
    }
    const areNodesToRenderLeavesOnly = nodesToRender.every((node) => !node.children || node.children.length === 0)
    return (
        <AntTreeSelect
            {...{
                ...treeConfig,
                popupClassName: areNodesToRenderLeavesOnly ? 'ant-tree-select__leaves-only' : '',
                onChange: (val: string | string[], labelList, extra) => {
                    return treeConfig.onChange(ensureArray(val) ?? [], labelList, extra)
                },

                value: treeConfig.multiple ? treeConfig.value : (treeConfig.value as any)[0],
            }}
        />
    )

    function convertSimpleData1DArrayToDisplayable(data: TreeSelectOption[]) {
        return sortOptions(data).map(withChildrenCount).map(withImageFallback)
    }

    function ensureUniqValues(value: TreeSelectOption) {
        if (!props.multiParents) {
            return value
        }
        const valueFieldName = staticConfig.fieldNames.value
        return { ...value, [valueFieldName]: value[valueFieldName] + uuidv4() }
    }

    /**used for hierarchical treedata */
    function convertTreeDataMultiDimensionArrayToDisplayable(data: TreeSelectOption[]): TreeSelectOption[] {
        const valueFieldName = staticConfig.fieldNames.value
        return sortOptions(data)
            .map(withImageFallback)
            .map(ensureUniqValues)
            .map(ensureKey(valueFieldName))
            .map((node) =>
                node.children
                    ? {
                          ...node,
                          // @ts-ignore
                          originalLabel: node[staticConfig.fieldNames.label],
                          [staticConfig.fieldNames.label]: LabelWithChildrenCount(
                              // @ts-ignore
                              node[staticConfig.fieldNames.label],
                              getLeafDescendantCount(node)
                          ),
                          children: convertTreeDataMultiDimensionArrayToDisplayable(node.children),
                      }
                    : {
                          ...node,
                          // @ts-ignore
                          originalLabel: node[staticConfig.fieldNames.label],
                      }
            )

        function getLeafDescendantCount(node: TreeSelectOption): number {
            return node.children?.flatMap(falattenTreeOption).filter(({ children }) => !children?.length).length
        }
    }

    function sortOptions(data: TreeSelectOption[]): TreeSelectOption[] {
        if (!props.sortTreeDataAlphabetically) {
            return data
        }
        const labelFieldName = staticConfig.fieldNames.label
        return data.sort((a, b) => a[labelFieldName]?.localeCompare(b[labelFieldName]))
    }

    function dropdownRender(menu: React.ReactElement<any, string | React.JSXElementConstructor<any>>) {
        return PreventClickPropagation(
            <>
                {hasCategories(props) ? (
                    <CategoryPicker
                        onCategoriesSelected={(ev) => setSelectedCategories(ev.detail)}
                        categories={[...props.categories]}
                        selected={selectedCategories}
                    />
                ) : null}
                {!!props.loading ? <Pulse /> /* why are we even showing this if input isloading? */ : menu}
            </>
        )
    }

    function tagRender(customTagProps: CustomTagProps) {
        const [shouldRender, setShouldRender] = React.useState(!props.multiParents)
        const { value, closable, onClose, label } = customTagProps
        const labelToUse = () => findOriginalLabelForSelection(value) ?? label.toString()

        useEffect(() => {
            if (props.multiParents && value) {
                hasTagNotBeenRendered(value).then(setShouldRender)
            }
        }, [])

        return shouldRender
            ? PreventClickPropagation(<Tag isRemovable={closable} onRemove={() => onClose()} label={labelToUse()} />)
            : null
    }

    /** needed when category filter is enabled and the selected item is not in the treeData passed to AntTreeSelect.
     * otherwise value would be displayed instead of label */
    function findOriginalLabelForSelection(value: any): string {
        const foundValue = (props.treeDataSimpleMode ? props.treeData : flattenedData).find((node) => {
            return node[staticConfig.fieldNames.value] === value
        })
        return foundValue?.originalLabel
    }

    function falattenTreeOption(treeNode: TreeSelectOption): TreeSelectOption[] {
        return [treeNode, ...(treeNode[staticConfig.fieldNames.children] ?? []).flatMap(falattenTreeOption)]
    }

    function withChildrenCount(node: TreeSelectOption, _index: number, simpleData: TreeSelectOption[]) {
        const simpleModeConfig = staticConfig.treeDataSimpleMode as SimpleModeConfig
        const leafDescendantCount = getChildren(node, simpleData).filter(isLeaf).length
        const label = leafDescendantCount
            ? LabelWithChildrenCount(node[staticConfig.fieldNames.label], leafDescendantCount)
            : node.label
        return {
            ...node,
            id: node.value,
            label,
            originalLabel: node[staticConfig.fieldNames.label],
            pId: node.parent ?? '',
        }

        function isLeaf(node: TreeSelectOption) {
            return !simpleData.some(
                (potentialChild) =>
                    potentialChild[simpleModeConfig?.pId ?? 'pId'] === node[simpleModeConfig?.id ?? 'id']
            )
        }
    }

    function getChildren(parentNode: TreeSelectOption, simpleData: TreeSelectOption[]): TreeSelectOption[] {
        const simpleModeConfig = staticConfig.treeDataSimpleMode as SimpleModeConfig
        return simpleData.filter(isChild).flatMap((node) => [node, ...getChildren(node, simpleData)])

        function isChild(node: TreeSelectOption) {
            return node[simpleModeConfig?.pId ?? 'pId'] === parentNode[simpleModeConfig?.id ?? 'id']
        }
    }
}

function LabelWithChildrenCount(label: string, childrenCount: number) {
    return !!childrenCount ? (
        <>
            {label} <span className="ant-tree-select__leaf-count">({childrenCount})</span>
        </>
    ) : (
        label
    )
}

function ensureKey(valueFieldName: string) {
    return (option: TreeSelectOption) => ({ key: option[valueFieldName], ...option })
}

function hasCategories(props: Props) {
    return !!props.categories?.length
}

function byCategory(selectedCategories: string[], hasCategories: boolean) {
    return (node: TreeSelectOption) => {
        return !hasCategories || selectedCategories.includes(node.category)
    }
}

function PreventClickPropagation(
    content: React.ReactElement<any, string | React.JSXElementConstructor<any>>
): React.ReactElement<any, string | React.JSXElementConstructor<any>> {
    return (
        <div
            onMouseDown={(e) => {
                e.stopPropagation()
                e.preventDefault()
            }}
        >
            {content}
        </div>
    )
}

function defaultNotFoundContent(query: string) {
    if (query.length > 0) {
        return (
            <div className="ant-tree-select__empty-container">
                <zaptic-icon icon-name="empty-results" class="ant-tree-select__empty-icon" />
                {Translations.t('shared:noSearchResults')}
            </div>
        )
    }

    return (
        <div className="ant-tree-select__empty-container">
            <zaptic-icon icon-name="empty-box" className="ant-tree-select__empty-icon" />
            <div>{Translations.t('shared:noItemToDisplay')}</div>
        </div>
    )
}

function withImageFallback(node: TreeSelectOption) {
    if (node.hideIcon) {
        return node
    }

    return {
        ...node,
        icon: node.icon || <zaptic-icon icon-name="missing-unit" />, // todo implement broken src fallback?
    }
}
